import React, { useRef, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import './StarfieldCanvas.css';

var TAU = Math.PI * 2;

function createRandom(seed) {
  var value = seed >>> 0;
  return function () {
    value += 0x6D2B79F5;
    var t = value;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function InfiniteStarfield(canvas, options) {
  this.canvas = canvas;
  this.ctx = canvas.getContext('2d', { alpha: true });
  this.options = options || {};
  this.seed = this.options.seed || 42;
  this.random = createRandom(this.seed);
  this.stars = [];
  this.meteors = [];
  this.width = 0;
  this.height = 0;
  this.dpr = 1;
  this.lastTime = 0;
  this.raf = 0;
  this.nextMeteor = 0;
  this.media = window.matchMedia('(prefers-reduced-motion: reduce)');
  this.boundResize = this.resize.bind(this);
  this.boundFrame = this.frame.bind(this);
  this.boundMotionChange = this.handleMotionChange.bind(this);
  window.addEventListener('resize', this.boundResize, { passive: true });
  if (this.media.addEventListener) this.media.addEventListener('change', this.boundMotionChange);
  this.resize();
  this.handleMotionChange();
}

InfiniteStarfield.prototype.destroy = function () {
  cancelAnimationFrame(this.raf);
  this.raf = 0;
  window.removeEventListener('resize', this.boundResize);
  if (this.media.removeEventListener) this.media.removeEventListener('change', this.boundMotionChange);
};

InfiniteStarfield.prototype.resize = function () {
  var rect = this.canvas.getBoundingClientRect();
  this.width = Math.max(1, Math.round(rect.width || window.innerWidth));
  this.height = Math.max(1, Math.round(rect.height || window.innerHeight));
  this.dpr = Math.min(window.devicePixelRatio || 1, 2);
  this.canvas.width = Math.round(this.width * this.dpr);
  this.canvas.height = Math.round(this.height * this.dpr);
  this.ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
  this.seedStars();
  this.draw(performance.now());
};

InfiniteStarfield.prototype.seedStars = function () {
  var area = this.width * this.height;
  var density = this.options.density || 8600;
  var count = Math.max(120, Math.min(this.options.maxStars || 300, Math.floor(area / density)));
  this.random = createRandom(this.seed + Math.round(this.width) + Math.round(this.height * 3));
  this.stars = [];
  for (var i = 0; i < count; i += 1) {
    var depth = this.random();
    var speed = 10 + depth * 30;
    var direction = -Math.PI / 4 + (this.random() - 0.5) * 0.32;
    this.stars.push({
      x: this.random() * this.width,
      y: this.random() * this.height,
      radius: 0.45 + depth * 1.75,
      alpha: 0.22 + depth * 0.58,
      vx: Math.cos(direction) * speed,
      vy: Math.sin(direction) * speed,
      phase: this.random() * TAU,
      pulse: 0.35 + this.random() * 0.85,
      hue: this.pickColor(depth, this.random()),
    });
  }
};

InfiniteStarfield.prototype.pickColor = function (depth, roll) {
  if (depth > 0.88 && roll > 0.62) return [61, 209, 231];
  if (depth > 0.72 && roll > 0.38) return [208, 182, 235];
  if (roll > 0.92) return [77, 171, 247];
  return [245, 240, 255];
};

InfiniteStarfield.prototype.handleMotionChange = function () {
  if (this.media.matches) {
    cancelAnimationFrame(this.raf);
    this.raf = 0;
    this.draw(performance.now());
    return;
  }
  if (!this.raf) {
    this.lastTime = performance.now();
    this.raf = requestAnimationFrame(this.boundFrame);
  }
};

InfiniteStarfield.prototype.wrap = function (value, max) {
  value %= max;
  return value < 0 ? value + max : value;
};

InfiniteStarfield.prototype.frame = function (now) {
  this.raf = requestAnimationFrame(this.boundFrame);
  var dt = Math.min(0.05, Math.max(0.001, (now - this.lastTime) / 1000));
  this.lastTime = now;
  this.update(dt, now);
  this.draw(now);
};

InfiniteStarfield.prototype.update = function (dt, now) {
  for (var i = 0; i < this.stars.length; i += 1) {
    var star = this.stars[i];
    star.x = this.wrap(star.x + star.vx * dt, this.width);
    star.y = this.wrap(star.y + star.vy * dt, this.height);
  }
  if (!this.nextMeteor) this.nextMeteor = now + 1800 + this.random() * 3400;
  if (now > this.nextMeteor && this.meteors.length < 2) {
    this.spawnMeteor(now);
    this.nextMeteor = now + 3600 + this.random() * 8200;
  }
  this.meteors = this.meteors.filter(function (meteor) {
    meteor.life += dt;
    meteor.x += meteor.vx * dt;
    meteor.y += meteor.vy * dt;
    return meteor.life < meteor.duration;
  });
};

InfiniteStarfield.prototype.spawnMeteor = function () {
  var startX = this.random() * this.width * 0.78 + this.width * 0.12;
  var startY = -40 - this.random() * this.height * 0.16;
  this.meteors.push({
    x: startX,
    y: startY,
    vx: -220 - this.random() * 140,
    vy: 240 + this.random() * 180,
    length: 160 + this.random() * 120,
    life: 0,
    duration: 1.1 + this.random() * 0.55,
    alpha: 0.22 + this.random() * 0.24,
  });
};

InfiniteStarfield.prototype.drawStar = function (star, x, y, now) {
  var ctx = this.ctx;
  var pulse = 0.74 + Math.sin(now * 0.001 * star.pulse + star.phase) * 0.22;
  var alpha = Math.max(0.08, star.alpha * pulse);
  var color = star.hue;
  if (star.radius > 1.55) {
    var glow = ctx.createRadialGradient(x, y, 0, x, y, star.radius * 7);
    glow.addColorStop(0, 'rgba(' + color[0] + ',' + color[1] + ',' + color[2] + ',' + (alpha * 0.30) + ')');
    glow.addColorStop(1, 'rgba(' + color[0] + ',' + color[1] + ',' + color[2] + ',0)');
    ctx.fillStyle = glow;
    ctx.beginPath();
    ctx.arc(x, y, star.radius * 7, 0, TAU);
    ctx.fill();
  }
  ctx.fillStyle = 'rgba(' + color[0] + ',' + color[1] + ',' + color[2] + ',' + alpha + ')';
  ctx.beginPath();
  ctx.arc(x, y, star.radius, 0, TAU);
  ctx.fill();
};

InfiniteStarfield.prototype.drawWrappedStar = function (star, now) {
  var margin = 16;
  var xs = [star.x];
  var ys = [star.y];
  if (star.x < margin) xs.push(star.x + this.width);
  if (star.x > this.width - margin) xs.push(star.x - this.width);
  if (star.y < margin) ys.push(star.y + this.height);
  if (star.y > this.height - margin) ys.push(star.y - this.height);
  for (var xi = 0; xi < xs.length; xi += 1) {
    for (var yi = 0; yi < ys.length; yi += 1) this.drawStar(star, xs[xi], ys[yi], now);
  }
};

InfiniteStarfield.prototype.drawMeteors = function () {
  var ctx = this.ctx;
  ctx.save();
  ctx.lineCap = 'round';
  for (var i = 0; i < this.meteors.length; i += 1) {
    var meteor = this.meteors[i];
    var progress = meteor.life / meteor.duration;
    var alpha = meteor.alpha * Math.sin(Math.PI * progress);
    var angle = Math.atan2(meteor.vy, meteor.vx);
    var tx = Math.cos(angle) * meteor.length;
    var ty = Math.sin(angle) * meteor.length;
    var gradient = ctx.createLinearGradient(meteor.x, meteor.y, meteor.x - tx, meteor.y - ty);
    gradient.addColorStop(0, 'rgba(208,182,235,' + alpha + ')');
    gradient.addColorStop(0.45, 'rgba(61,209,231,' + (alpha * 0.35) + ')');
    gradient.addColorStop(1, 'rgba(61,209,231,0)');
    ctx.strokeStyle = gradient;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(meteor.x, meteor.y);
    ctx.lineTo(meteor.x - tx, meteor.y - ty);
    ctx.stroke();
  }
  ctx.restore();
};

InfiniteStarfield.prototype.draw = function (now) {
  var ctx = this.ctx;
  ctx.clearRect(0, 0, this.width, this.height);
  ctx.save();
  ctx.globalCompositeOperation = 'lighter';
  for (var i = 0; i < this.stars.length; i += 1) this.drawWrappedStar(this.stars[i], now);
  if (!this.media.matches) this.drawMeteors();
  ctx.restore();
};

export default function StarfieldCanvas() {
  const { theme } = useTheme();
  const canvasRef = useRef(null);

  useEffect(() => {
    if (theme !== 'dark' || !canvasRef.current) return;
    const sf = new InfiniteStarfield(canvasRef.current, { seed: 301, density: 7600, maxStars: 340 });
    return () => sf.destroy();
  }, [theme]);

  if (theme !== 'dark') return null;

  return (
    <div className="starfield-layer" aria-hidden="true">
      <canvas ref={canvasRef} className="starfield-canvas" />
    </div>
  );
}
